import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { CalculationsService } from '@/services/calculations'
import type { Trade } from '@/lib/types'
import { formatCurrency, cn } from '@/lib/utils'

interface Props {
  trades: Trade[]
  currentBalance: number
}

export function EquityCurveChart({ trades, currentBalance }: Props) {
  const equityCurve = CalculationsService.calculateEquityCurve(trades, currentBalance)

  const summary = useMemo(() => {
    if (equityCurve.length === 0) return { min: 0, max: 0, start: 0, end: 0, change: 0, changePercent: 0 }
    const balances = equityCurve.map((d) => d.balance)
    const start = balances[0]
    const end = balances[balances.length - 1]
    const change = end - start
    const changePercent = start > 0 ? (change / start) * 100 : 0
    return {
      min: Math.min(...balances),
      max: Math.max(...balances),
      start,
      end,
      change,
      changePercent,
    }
  }, [equityCurve])

  const chartData = useMemo(() => {
    return equityCurve.map((d) => ({
      ...d,
      date: new Date(d.date + 'T12:00:00').toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'America/Sao_Paulo',
      }),
      rawDate: d.date,
    }))
  }, [equityCurve])

  const isPositive = summary.change >= 0
  const chartColor = isPositive ? 'oklch(0.765 0.177 163.223)' : 'oklch(0.637 0.237 25.331)'
  const gradientId = 'equityGrad'

  if (equityCurve.length === 0) {
    return (
      <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/10 to-muted/20">
        <div className="flex h-64 items-center justify-center">
          <div className="text-center">
            <Minus className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Nenhuma operação encontrada</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="group relative overflow-hidden rounded-xl border border-border/50 bg-gradient-to-br from-muted/10 to-muted/20 transition-all hover:border-border">
      {/* Header Stats */}
      <div className="p-5 pb-0">
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* Resumo Principal */}
          <div className="flex items-center gap-6">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Variação no Período</p>
              <div className="flex items-center gap-2">
                {isPositive ? (
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-rose-500" />
                )}
                <span className={cn(
                  "text-xl font-bold",
                  isPositive ? "text-emerald-500" : "text-rose-500"
                )}>
                  {isPositive ? '+' : ''}{formatCurrency(summary.change)}
                </span>
                <span className={cn(
                  "text-sm font-medium px-2 py-0.5 rounded-full",
                  isPositive ? "bg-emerald-500/10 text-emerald-500" : "bg-rose-500/10 text-rose-500"
                )}>
                  {isPositive ? '+' : ''}{summary.changePercent.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Min/Max */}
          <div className="flex gap-4 text-sm">
            <div className="text-right">
              <p className="text-xs text-muted-foreground mb-0.5">Mínimo</p>
              <p className="font-semibold text-rose-500">{formatCurrency(summary.min)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground mb-0.5">Máximo</p>
              <p className="font-semibold text-emerald-500">{formatCurrency(summary.max)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-5 pt-4">
        <div className="h-[260px] sm:h-[300px] lg:h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={chartColor} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid
                vertical={false}
                strokeDasharray="2 2"
                stroke="oklch(0.3 0.01 286 / 0.3)"
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: 'oklch(0.705 0.015 286.067)' }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'oklch(0.705 0.015 286.067)' }}
                axisLine={false}
                tickLine={false}
                width={70}
                tickFormatter={(v) => formatCurrency(Number(v))}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null
                  const value = payload[0]?.value as number
                  return (
                    <div className="rounded-xl border border-border bg-card/95 backdrop-blur-sm px-4 py-3 shadow-xl">
                      <p className="text-xs text-muted-foreground mb-1">{label}</p>
                      <p className="text-lg font-bold text-foreground">
                        {formatCurrency(value)}
                      </p>
                    </div>
                  )
                }}
                cursor={{ fill: '#d1d5db', opacity: 0.1 }}
              />
              <Area
                type="monotone"
                dataKey="balance"
                name="Saldo"
                stroke={chartColor}
                fill={`url(#${gradientId})`}
                strokeWidth={2.5}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
